type paramsType = {
  slug: string
}

type paramsPricesType = paramsType & {
  province: string
  dateIn?: string
  dateOut?: string
  capacity?: string
  cos?: string
}

type paramsCottageDetail = paramsType & {
  province: string
}

type paramsCottageDeal = paramsCottageDetail & {
  id: string
}

type paramsDeal = paramsType & {
  id: string
}

type paramsFilteredCottage = {
  url: string
  dateIn?: string
  dateOut?: string
  capacity?: string
}

type paramsCottageList = {
  l?: string
  q?: string
  d?: string
  c?: string
}

/* OWNER ADMIN */
type paramsOwnerType = {
  ownerId: string
}
type paramsCottageType = paramsOwnerType & {
  cottageId: string
}
type paramsRentUnitType = paramsOwnerType & {
  rentUnitId: string
}

/* TRAVELER ADMIN: */
type paramsTravelerType = {
  travelerId: string
}

type paramsTravelerTypeItem = paramsTravelerType & {
  itemId: string
}

type paramsTravelerMessagesListType = paramsTravelerType & {
  page?: number
}

type paramsTravelerMessagesByIdType = paramsTravelerType & {
  id: string
}

type paramsTravelerMessagesType = paramsTravelerType & {
  ownerId: string
}

/* PAYMENT FUNNEL */
type paramsFunnelType = paramsType & {
  province: string
  id: string
  message?: string
  error?: string
}

type paramsPaymentSuccess = paramsType & {
  province: string
  id: string
  slug: string
}

export default function () {
  const runtimeConfig = useRuntimeConfig()

  const DOMAIN = `${runtimeConfig.public.PROTOCOL}${runtimeConfig.public.DOMAIN_ES}`

  const DOMAIN_HELP = 'https://ayuda.escapadarural.com'

  const addDomain = (params: paramsType): string => {
    return `${DOMAIN}${params.slug}`
  }

  const deleteDomain = (url: URL): string => {
    return url.toString().substring(url.origin.length)
  }

  const home = (): string => {
    const rawURL = new URL('', DOMAIN)
    return deleteDomain(rawURL)
  }

  const holidaysList = (): string => {
    const rawURL = new URL('/vacaciones', DOMAIN)
    return deleteDomain(rawURL)
  }

  const holidaysDetail = (params: paramsType): string => {
    const rawURL = new URL(`/vacaciones/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const villagesList = (): string => {
    const rawURL = new URL('/pueblos', DOMAIN)
    return deleteDomain(rawURL)
  }

  const villagesDetail = (params: paramsType): string => {
    const rawURL = new URL(`/pueblos/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const escapadasList = (): string => {
    const rawURL = new URL('/escapadas', DOMAIN)
    return deleteDomain(rawURL)
  }

  const escapadasDetail = (params: paramsType): string => {
    const rawURL = new URL(`/escapadas/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const prices = (params: paramsPricesType): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/precios`,
      DOMAIN,
    )

    params.dateIn && rawURL.searchParams.set('dateIn', params.dateIn)
    params.dateOut && rawURL.searchParams.set('dateOut', params.dateOut)
    params.capacity && rawURL.searchParams.set('capacity', params.capacity)
    params.cos && rawURL.searchParams.set('cos', params.cos)

    return deleteDomain(rawURL)
  }

  const dealsList = (): string => {
    const rawURL = new URL('/ofertas-casas-rurales', DOMAIN)
    return deleteDomain(rawURL)
  }

  const dealDetail = (params: paramsDeal): string => {
    const rawURL = new URL(`/oferta/${params.slug}/${params.id}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const cottageDetail = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContact = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/contacto-email`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactWhatsApp = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/contacto-whatsapp`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactDeal = (params: paramsCottageDeal): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/oferta/${params.id}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactPrebooking = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/pre-reserva`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottagePhones = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/private/casa-rural/${params.province}/${params.slug}/telefono`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const allCottageReviews = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/opiniones`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageReviewRate = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/crear-opinion`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const filteredCottage = (params: paramsFilteredCottage): string => {
    const rawURL = new URL(params.url, DOMAIN)

    params.dateIn && rawURL.searchParams.set('dateIn', params.dateIn)
    params.dateOut && rawURL.searchParams.set('dateOut', params.dateOut)
    params.capacity && rawURL.searchParams.set('capacity', params.capacity)

    return deleteDomain(rawURL)
  }

  const cottageList = (params?: paramsCottageList): string => {
    const rawURL = new URL('/resultados', DOMAIN)

    if (params?.l) rawURL.searchParams.set('l', params.l)
    if (params?.q) rawURL.searchParams.set('q', params.q)
    if (params?.d) rawURL.searchParams.set('d', params.d)
    if (params?.c) rawURL.searchParams.set('c', params.c)

    return deleteDomain(rawURL)
  }

  /* TODO: Deprecated in favor of cottageDefinedList */
  const cottageProvince = (params: paramsType): string => {
    const rawURL = new URL(`/casas-rurales/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const cottageDefinedList = (params: paramsType): string => {
    const rawURL = new URL(`/casas-rurales/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const mostWantedList = (): string => {
    const rawURL = new URL('/lo-mas-buscado', DOMAIN)
    return deleteDomain(rawURL)
  }

  const magazine = (): string => {
    const rawURL = new URL('/blog', DOMAIN)
    return deleteDomain(rawURL)
  }

  const help = (): string => {
    const rawURL = new URL('/hc/es/requests/new', DOMAIN_HELP)
    return rawURL.href
  }

  const helpArticle = (): string => {
    const rawURL = new URL('/hc/es/articles/13929265873053', DOMAIN_HELP)
    return rawURL.href
  }

  /* TRAVELER ADMIN: */
  const travelerBooking = (params: paramsTravelerTypeItem): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/online-booking/view/${params.itemId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerMessagesList = (
    params: paramsTravelerMessagesListType,
  ): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/messages`,
      DOMAIN,
    )

    if (params.page) rawURL.searchParams.set('page', params.page.toString())
    return deleteDomain(rawURL)
  }

  const travelerMessagesById = (
    params: paramsTravelerMessagesByIdType,
  ): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/conversation/${params.id}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerMessages = (params: paramsTravelerMessagesType): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/conversation/${params.ownerId}${params.travelerId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerFavorites = (params: paramsTravelerType): string => {
    const rawURL = new URL(`/viajero/${params.travelerId}/listas`, DOMAIN)
    return deleteDomain(rawURL)
  }

  /* OWNER ADMIN */
  const ownerNewCottage = (params: paramsOwnerType): string => {
    const rawURL = new URL(`/propietario/${params.ownerId}/casa/nueva`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const moreInfoDsa = (): string => {
    const rawURL = new URL(
      '/hc/es/articles/21054409035677-Informaci%C3%B3n-sobre-el-Reglamento-de-Servicios-Digitales',
      DOMAIN_HELP,
    )
    return rawURL.href
  }

  const onlineBookingConfig = (params: paramsOwnerType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/online-booking/config`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarIcal = (params: paramsRentUnitType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/sync/${params.rentUnitId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarIcalNew = (params: paramsRentUnitType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/sync/${params.rentUnitId}/new`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const useConditions = (): string => {
    const rawURL = new URL('/aviso-legal', DOMAIN)
    return deleteDomain(rawURL)
  }

  const privacyPolicy = (): string => {
    const rawURL = new URL('/politica-privacidad', DOMAIN)
    return deleteDomain(rawURL)
  }

  const photosUpload = (params: paramsCottageType): string => {
    const rawURL = new URL(
      `/propietario/${params.ownerId}/casa/${params.cottageId}/upload/fotos`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCottageDetail = (params: paramsCottageType): string => {
    const rawURL = new URL(
      `/propietario/${params.ownerId}/casa/${params.cottageId}/editar/datos-basicos`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const rememberPassword = (): string => {
    const rawURL = new URL('/viajero/recordar-password', DOMAIN)
    return deleteDomain(rawURL)
  }

  /* PAYMENT FUNNEL */
  const paymentFunnel = (params: paramsFunnelType): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking/${params.id}`,
      DOMAIN,
    )

    if (params.message) rawURL.searchParams.set('message', params.message)
    if (params.error) rawURL.searchParams.set('error', params.error)

    return rawURL.toString()
  }

  const paymentSuccess = (params: paramsPaymentSuccess): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking/${params.id}/payment-success`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  /* AUTH  */
  const login = (): string => {
    const rawURL = new URL('/login', DOMAIN)
    return deleteDomain(rawURL)
  }

  const travelerRegistration = (): string => {
    const rawURL = new URL('/viajero/registro', DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerRegistration = (): string => {
    const rawURL = new URL('/propietario/casa/nueva', DOMAIN)
    return deleteDomain(rawURL)
  }

  const calendarQuickRentUnit = (params: paramsOwnerType): string => {
    return `${DOMAIN}/owner/${params.ownerId}/calendar-quick/rent-unit`
  }

  const ownerQualityRate = (params: paramsCottageType): string => {
    const rawURL = new URL(`/owner/${params.ownerId}/quality-rate`, DOMAIN)

    rawURL.searchParams.set('cottageId', params.cottageId)

    return deleteDomain(rawURL)
  }

  return {
    addDomain,
    deleteDomain,
    home,
    holidaysList,
    holidaysDetail,
    villagesList,
    villagesDetail,
    escapadasList,
    escapadasDetail,
    prices,
    dealsList,
    dealDetail,
    cottageDetail,
    allCottageReviews,
    cottageReviewRate,
    filteredCottage,
    cottageList,
    cottageProvince,
    cottageDefinedList,
    ownerNewCottage,
    mostWantedList,
    magazine,
    help,
    helpArticle,
    travelerBooking,
    travelerMessagesList,
    travelerMessagesById,
    travelerMessages,
    travelerFavorites,
    useConditions,
    privacyPolicy,
    photosUpload,
    ownerCottageDetail,
    rememberPassword,
    paymentFunnel,
    paymentSuccess,
    moreInfoDsa,
    onlineBookingConfig,
    ownerCalendarIcal,
    ownerCalendarIcalNew,
    login,
    travelerRegistration,
    ownerRegistration,
    cottageContact,
    cottageContactWhatsApp,
    cottageContactDeal,
    cottageContactPrebooking,
    cottagePhones,
    calendarQuickRentUnit,
    ownerQualityRate,
  }
}
